// AAR (After Action Review) — kobler den ferdige, deterministiske
// `generateAar()` (@skade/contracts) til en faktisk flate i COP.
//
// AAR-01: `generateAar()` hadde tester, men NULL kallsteder — ingen operatør
// kunne generere en AAR. Denne fila er derfor MAPPING + tynt DOM-lag, ikke en
// ny aggregering: hendelsesstrømmen finnes allerede i `hendelseslogg.ts`/
// `main.ts` (se filhodet der: «historikken finnes i meldingslista,
// tidsmaskinen og hubben»).
//
// ⚠ `AarEvent.actor` settes IKKE her. `Hendelse` har ikke et rendyrket
// avsenderfelt — avsender står ofte som fritekst inni `detalj` (f.eks.
// «ola / Lag 2 · sendt», «@ola:hv (mottatt)», «requester · priority ·
// status»), i ulikt format per hendelsestype. Å parse det ut med regex ville
// vært en GJETNING framstilt som et faktum, og det er nøyaktig det grunnlovens
// §1 forbyr («ingen mocks/placeholder som utgir seg for å være ferdig»). AAR-en
// er fortsatt nyttig uten aktør per hendelse — `byActor` i kontrakten blir
// bare tom, det er en ærlig begrensning, ikke en feil.
import { generateAar, aarToMarkdown, type AarEvent, type AarInput, type Aar } from '@skade/contracts';
import type { Hendelse } from './hendelseslogg.js';

/**
 * Hastegrad → vekt. Samme skala som kvalifiserer en hendelse til haste-båndet
 * (`HASTEBAND_NIVAA` i `hendelseslogg.ts`): FLASH/IMMEDIATE er nøkkelhendelse-
 * kandidater i AAR-en (`generateAar` plukker vekt ≥ 2) på samme grunnlag som de
 * er nøkkelhendelser i den levende loggen — én skala, ikke to.
 */
const HASTEGRAD_VEKT: Record<string, number> = { flash: 3, immediate: 2, priority: 1, routine: 0 };

/**
 * Map fra COP sin hendelseslogg til AAR-kontraktens hendelsesform.
 *
 * Ren funksjon — testbar uten DOM. `kind` er `Hendelse.type` rått (f.eks.
 * 'rapport'/'varsel'/'perstat'), IKKE den norske etiketten
 * (`HENDELSE_ETIKETT`): AAR-kontrakten sier selv at `kind` er en fri
 * type-streng, og å oversette den her ville skjult hvilken faktisk
 * `HendelseType` en linje kom fra.
 */
export function hendelserTilAarEvents(hendelser: Hendelse[]): AarEvent[] {
  return hendelser.map((h) => ({
    tsMs: h.tsMs,
    kind: h.type,
    summary: h.detalj ? `${h.tittel} — ${h.detalj}` : h.tittel,
    weight: h.hastegrad ? HASTEGRAD_VEKT[h.hastegrad] : undefined,
  }));
}

export interface AarPanelHooks {
  /** Hele hendelsesloggen appen sitter med akkurat nå (samme kilde `shell.setHendelser` males fra). */
  getHendelser: () => Hendelse[];
  /** Overstyrbar «nå» (test); brukes til å forhåndsutfylle tidsvinduets slutt. */
  naa?: () => number;
  /** Enhet/avdeling forhåndsutfylt i AAR-hodet, når konteksten er kjent. */
  enhet?: () => string;
}

export class AarPanel {
  readonly el: HTMLElement;
  private open = false;
  private startEl!: HTMLInputElement;
  private endEl!: HTMLInputElement;
  private titleEl!: HTMLInputElement;
  private unitEl!: HTMLInputElement;
  private sustainEl!: HTMLTextAreaElement;
  private improveEl!: HTMLTextAreaElement;
  private resultEl!: HTMLElement;
  private feilEl!: HTMLElement;
  private sisteGenererte: Aar | null = null;

  constructor(private hooks: AarPanelHooks) {
    this.el = this.build();
  }

  toggle() {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) this.forhaandsutfyll();
  }

  /** Siste genererte AAR — til test (og en eventuell senere «eksporter»-knapp). */
  get sisteAar(): Aar | null { return this.sisteGenererte; }

  private build(): HTMLElement {
    const p = div('report-composer aar-panel');
    const head = div('report-head');
    head.append(span('report-title', 'AAR — After Action Review'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×';
    x.title = 'Lukk';
    x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);

    const form = div('report-form');

    const tittelFelt = felt('AAR-tittel');
    this.titleEl = tittelFelt.input as HTMLInputElement;
    this.titleEl.value = 'AAR';
    form.append(tittelFelt.el);

    const enhetFelt = felt('Enhet');
    this.unitEl = enhetFelt.input as HTMLInputElement;
    form.append(enhetFelt.el);

    const tidRad = div('aar-tidsvindu');
    const fraFelt = felt('Fra', 'datetime-local');
    this.startEl = fraFelt.input as HTMLInputElement;
    const tilFelt = felt('Til', 'datetime-local');
    this.endEl = tilFelt.input as HTMLInputElement;
    tidRad.append(fraFelt.el, tilFelt.el);
    form.append(tidRad);

    const hint = document.createElement('div');
    hint.className = 'report-hint';
    hint.textContent = 'Tidsvinduet er forhåndsutfylt med hele den tilgjengelige loggen — juster «Fra»/«Til» for et kortere utsnitt.';
    form.append(hint);

    // Objectives/sustain/improve er VALGFRIE i AarInput. Objectives (mål +
    // «nådd/ikke nådd» per punkt) er utelatt her med vilje: et fritekstfelt for
    // sustain/improve gir verdi uten UI for en liste av avkryssbare mål, og AAR-
    // ens kjernefunksjon — bygg fra hendelser — skal ikke vente på det.
    const sustainFelt = felt('Behold (sustain) — én per linje', 'textarea');
    this.sustainEl = sustainFelt.input as HTMLTextAreaElement;
    form.append(sustainFelt.el);

    const improveFelt = felt('Forbedre (improve) — én per linje', 'textarea');
    this.improveEl = improveFelt.input as HTMLTextAreaElement;
    form.append(improveFelt.el);

    this.feilEl = span('report-error', '');
    this.feilEl.hidden = true;
    form.append(this.feilEl);

    const submit = document.createElement('button');
    submit.className = 'report-submit'; submit.textContent = 'Generer AAR';
    submit.onclick = () => this.generer();
    form.append(submit);

    p.append(form);

    this.resultEl = div('aar-resultat');
    this.resultEl.hidden = true;
    p.append(this.resultEl);

    return p;
  }

  /**
   * Fyller tidsvinduet med hele det tilgjengelige hendelsessettet ved åpning —
   * «hele skiftet» er det vanligste ønsket, og operatøren skal ikke måtte slå
   * opp klokkeslett selv for det. Overskriver ikke et valg operatøren allerede
   * har gjort (f.eks. hvis panelet lukkes og åpnes igjen).
   */
  private forhaandsutfyll() {
    const hendelser = this.hooks.getHendelser();
    if (hendelser.length && !this.startEl.value && !this.endEl.value) {
      const ts = hendelser.map((h) => h.tsMs);
      this.startEl.value = toLocalInput(Math.min(...ts));
      this.endEl.value = toLocalInput(this.hooks.naa?.() ?? Math.max(...ts));
    }
    if (this.hooks.enhet && !this.unitEl.value) {
      const e = this.hooks.enhet();
      if (e) this.unitEl.value = e;
    }
  }

  private generer() {
    const startMs = fromLocalInput(this.startEl.value);
    const endMs = fromLocalInput(this.endEl.value);
    if (startMs === null || endMs === null || endMs < startMs) {
      this.feilEl.hidden = false;
      this.feilEl.textContent = 'Ugyldig tidsvindu — «Til» må være etter «Fra», og begge må være satt.';
      return;
    }
    this.feilEl.hidden = true;
    const alle = this.hooks.getHendelser();
    const events = hendelserTilAarEvents(alle.filter((h) => h.tsMs >= startMs && h.tsMs <= endMs));
    const input: AarInput = {
      title: this.titleEl.value.trim() || 'AAR',
      unit: this.unitEl.value.trim() || 'ikke satt',
      startMs, endMs, events,
      sustain: linjer(this.sustainEl.value),
      improve: linjer(this.improveEl.value),
    };
    const aar = generateAar(input);
    this.sisteGenererte = aar;
    this.resultEl.hidden = false;
    this.resultEl.replaceChildren();
    this.resultEl.append(span('aar-meta', `${aar.eventCount} hendelser i vinduet · ${aar.durationMin} min`));
    const pre = document.createElement('pre');
    pre.className = 'aar-markdown';
    pre.textContent = aarToMarkdown(aar);
    this.resultEl.append(pre);
  }
}

function felt(
  label: string,
  kind: 'text' | 'textarea' | 'datetime-local' = 'text',
): { el: HTMLElement; input: HTMLInputElement | HTMLTextAreaElement } {
  const wrap = div('report-field');
  wrap.append(span('report-field-label', label));
  const input = kind === 'textarea' ? document.createElement('textarea') : document.createElement('input');
  input.className = 'report-input';
  if (kind !== 'textarea') (input as HTMLInputElement).type = kind;
  wrap.append(input);
  return { el: wrap, input };
}

function linjer(v: string): string[] {
  return v.split('\n').map((s) => s.trim()).filter(Boolean);
}

/** `datetime-local`-verdi (lokal tid, uten sekunder) fra epoke-ms. */
function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Epoke-ms fra en `datetime-local`-verdi, eller `null` om feltet er tomt/ugyldig. */
function fromLocalInput(v: string): number | null {
  if (!v) return null;
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
